// The Wins page's rules: what the API serves, how an impact becomes the card's big figure, how a share
// caption is built and which wins a period shows. Import-free on purpose (see "Tests (jest)" in
// orion-web's CLAUDE.md): `/api/wins` maps rows with `toWebWin`, the components format with the rest.

export type WinKind = 'CAUGHT' | 'PREVENTED' | 'IMPROVED' | 'HANDLED';
export type WinDomain = 'BUSINESS' | 'PERSONAL';

export type WebWinImpact =
  | { type: 'MONEY'; value: number; currency: string; estimated: boolean; note?: string }
  | { type: 'TIME'; minutes: number; estimated: boolean; note?: string }
  | { type: 'COUNT'; value: number; unit: string; estimated: boolean; note?: string }
  | { type: 'METRIC_CHANGE'; metric: string; before: string; after: string; unit?: string; estimated: boolean; note?: string };

export interface WebWin {
  id: string;
  kind?: WinKind;
  domain?: WinDomain;
  title: string;
  description?: string;
  /** What the user got out of it, in their language, as the agent named it. */
  gain?: string;
  /** ISO. */
  createdDate: string;
  impacts: WebWinImpact[];
  quote?: { text: string; attribution?: string };
}

export interface WinsPageDto {
  wins: WebWin[];
  nextCursor?: string;
}

const KINDS = new Set<string>(['CAUGHT', 'PREVENTED', 'IMPROVED', 'HANDLED']);
const DOMAINS = new Set<string>(['BUSINESS', 'PERSONAL']);

type Raw = Record<string, unknown>;
const obj = (v: unknown): Raw | undefined => (v && typeof v === 'object' ? (v as Raw) : undefined);
const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined);

/** int64 fields arrive as a string, a number or a Long-like object, depending on the transport. */
function num(v: unknown): number | undefined {
  if (typeof v === 'number') return Number.isFinite(v) ? v : undefined;
  if (typeof v === 'string' && v.trim()) {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }
  if (v && typeof v === 'object' && typeof (v as { toString?: unknown }).toString === 'function') {
    const n = Number(String(v));
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function iso(v: unknown): string | undefined {
  if (v instanceof Date) return Number.isFinite(v.valueOf()) ? v.toISOString() : undefined;
  if (typeof v === 'string') {
    const d = new Date(v);
    return Number.isFinite(d.valueOf()) ? d.toISOString() : undefined;
  }
  return undefined;
}

function toImpact(raw: unknown): WebWinImpact | undefined {
  const i = obj(raw);
  if (!i) return undefined;
  const estimated = i.estimated === true;
  const note = str(i.note);
  const extra = { estimated, ...(note ? { note } : {}) };
  const money = obj(i.moneyImpact);
  if (money) {
    const value = num(money.value);
    const currency = str(money.currency);
    return value !== undefined && currency ? { type: 'MONEY', value, currency, ...extra } : undefined;
  }
  const time = obj(i.timeImpact);
  if (time) {
    const minutes = num(time.minutes);
    return minutes && minutes > 0 ? { type: 'TIME', minutes, ...extra } : undefined;
  }
  const count = obj(i.countImpact);
  if (count) {
    const value = num(count.value);
    const unit = str(count.unit);
    return value !== undefined && unit ? { type: 'COUNT', value, unit, ...extra } : undefined;
  }
  const metric = obj(i.metricChangeImpact);
  if (metric) {
    const name = str(metric.metric);
    const before = str(metric.before);
    const after = str(metric.after);
    const unit = str(metric.unit);
    return name && before && after ? { type: 'METRIC_CHANGE', metric: name, before, after, ...(unit ? { unit } : {}), ...extra } : undefined;
  }
  return undefined;
}

/** One stored win as the page reads it, or null for a row the page cannot show (no id, no title). */
export function toWebWin(raw: unknown): WebWin | null {
  const w = obj(raw);
  const id = str(w?.id);
  const title = str(w?.title);
  const createdDate = iso(w?.createdDate);
  if (!w || !id || !title || !createdDate) return null;
  const kind = typeof w.kind === 'string' && KINDS.has(w.kind) ? (w.kind as WinKind) : undefined;
  const domain = typeof w.domain === 'string' && DOMAINS.has(w.domain) ? (w.domain as WinDomain) : undefined;
  const description = str(w.description);
  const gain = str(w.gain);
  const impacts = (Array.isArray(w.impacts) ? w.impacts : []).map(toImpact).filter((i): i is WebWinImpact => !!i);
  const quoteProof = (Array.isArray(w.proofs) ? w.proofs : []).map((p) => obj(obj(p)?.quoteProof)).find((q) => !!str(q?.text));
  const quoteText = str(quoteProof?.text);
  const attribution = str(quoteProof?.attribution);
  return {
    id,
    ...(kind ? { kind } : {}),
    ...(domain ? { domain } : {}),
    title,
    ...(description ? { description } : {}),
    ...(gain ? { gain } : {}),
    createdDate,
    impacts,
    ...(quoteText ? { quote: { text: quoteText, ...(attribution ? { attribution } : {}) } } : {}),
  };
}

// ── The figure ────────────────────────────────────────────────────────────────────────────────────

/** A working day, for turning a large time total into days. */
export const WORKDAY_MINUTES = 8 * 60;
/** From this many hours on, time reads in days. */
const DAYS_FROM_HOURS = 24;

export type TimeAmount = { unit: 'minutes' | 'hours' | 'days'; amount: number };

const roundHalf = (n: number) => Math.round(n * 2) / 2;

export function timeAmount(minutes: number): TimeAmount {
  if (minutes < 60) return { unit: 'minutes', amount: Math.max(1, Math.round(minutes)) };
  const hours = minutes / 60;
  if (hours >= DAYS_FROM_HOURS) return { unit: 'days', amount: roundHalf(minutes / WORKDAY_MINUTES) };
  return { unit: 'hours', amount: hours >= 10 ? Math.round(hours) : roundHalf(hours) };
}

/** The pieces of copy the figure needs; the component passes `t()` results in. */
export interface FigureCopy {
  minutes: (amount: string) => string;
  hours: (amount: string) => string;
  days: (amount: string) => string;
  timeLabel: string;
  moneyLabel: string;
  atStakeLabel: string;
}

export interface WinFigure {
  value: string;
  label: string;
  /** A long value (a money amount, "12 → 19") renders one size smaller, as in the design. */
  long: boolean;
  estimated: boolean;
}

function formatNumber(n: number, locale: string, maximumFractionDigits = 1): string {
  try {
    return new Intl.NumberFormat(locale, { maximumFractionDigits }).format(n);
  } catch {
    return String(n);
  }
}

function formatMoney(value: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
    }).format(value);
  } catch {
    return `${formatNumber(value, locale, 2)} ${currency}`;
  }
}

/**
 * The card shows a figure label in two places: its lead beside the number, and the rest in the ticket's
 * stub, so a long label never wraps into a narrow column next to the figure.
 */
export function winFigureParts(label: string): { lead: string; tail: string } {
  const at = label.indexOf(' · ');
  return at < 0 ? { lead: label, tail: '' } : { lead: label.slice(0, at), tail: label.slice(at + 3) };
}

/** The card's big number and the line under it, from the win's first impact that has one. */
export function winFigure(win: Pick<WebWin, 'impacts' | 'kind'>, copy: FigureCopy, locale: string): WinFigure | null {
  const impact = win.impacts[0];
  if (!impact) return null;
  const estimated = impact.estimated;
  switch (impact.type) {
    case 'TIME': {
      const { unit, amount } = timeAmount(impact.minutes);
      const value = copy[unit](formatNumber(amount, locale));
      return { value, label: impact.note ?? copy.timeLabel, long: false, estimated };
    }
    case 'MONEY': {
      const value = formatMoney(impact.value, impact.currency, locale);
      // A CAUGHT win's money is what stands to be lost or gained by acting on the catch, never money already
      // in hand, so it always says so, ahead of any note.
      const label =
        win.kind === 'CAUGHT'
          ? [copy.atStakeLabel, impact.note].filter(Boolean).join(' · ')
          : impact.note ?? copy.moneyLabel;
      return { value, label, long: value.length > 5, estimated };
    }
    case 'COUNT':
      return { value: formatNumber(impact.value, locale, 0), label: impact.unit, long: false, estimated };
    case 'METRIC_CHANGE': {
      const unit = impact.unit === 'percent' || impact.unit === '%' ? '%' : '';
      const label = unit || !impact.unit ? impact.metric : `${impact.metric} (${impact.unit})`;
      return { value: `${impact.before}${unit} → ${impact.after}${unit}`, label, long: true, estimated };
    }
    default:
      return null;
  }
}

// ── Sharing ──────────────────────────────────────────────────────────────────────────────────────

export const SHARE_HASHTAG = '#SymphonyByWix';

/** The caption the share dialog starts with: one sentence from the win, then the hashtag. */
export function buildWinCaption(sentence: string): string {
  const text = sentence.replace(/\s+/g, ' ').trim();
  return text ? `${text} ${SHARE_HASHTAG}` : SHARE_HASHTAG;
}

/**
 * The public share intents. Facebook takes only a link, so it shares the product page and the dialog
 * copies the caption for the reader to paste. Instagram has no web intent at all.
 */
export function shareIntentUrl(target: 'linkedin' | 'facebook' | 'x' | 'whatsapp', caption: string, productUrl: string): string {
  const text = encodeURIComponent(caption);
  switch (target) {
    case 'linkedin':
      return `https://www.linkedin.com/feed/?shareActive=true&text=${text}`;
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(productUrl)}`;
    case 'x':
      return `https://x.com/intent/post?text=${text}`;
    case 'whatsapp':
      return `https://wa.me/?text=${text}`;
  }
}

// ── Periods ──────────────────────────────────────────────────────────────────────────────────────

export type WinPeriod = 'all' | 'week' | 'month';

/** The current week (Monday to Sunday) or calendar month, in the reader's local time. */
export function periodRange(period: Exclude<WinPeriod, 'all'>, now: Date): { start: Date; end: Date } {
  if (period === 'month') {
    return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: new Date(now.getFullYear(), now.getMonth() + 1, 1) };
  }
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  const end = new Date(start);
  end.setDate(end.getDate() + 7);
  return { start, end };
}

/** The wins in a period, newest first. */
export function winsInPeriod<T extends Pick<WebWin, 'createdDate'>>(wins: readonly T[], period: WinPeriod, now: Date): T[] {
  const sorted = [...wins].sort((a, b) => Date.parse(b.createdDate) - Date.parse(a.createdDate));
  if (period === 'all') return sorted;
  const { start, end } = periodRange(period, now);
  return sorted.filter((w) => {
    const t = Date.parse(w.createdDate);
    return t >= start.valueOf() && t < end.valueOf();
  });
}

/** The minutes of work the period's wins took off the reader's plate. */
export function totalMinutes(wins: readonly Pick<WebWin, 'impacts'>[]): number {
  return wins.reduce((sum, w) => sum + w.impacts.reduce((s, i) => s + (i.type === 'TIME' ? i.minutes : 0), 0), 0);
}
